/**
 * Translation layer between the AI's diagram spec and Excalidraw's scene format.
 *
 * The model emits a small, forgiving JSON shape (see SCHEMA_DOC below) rather
 * than raw Excalidraw elements: Excalidraw's format has ~30 required fields per
 * element, bound-element back-references, and version/seed bookkeeping that a
 * language model gets wrong far too often. Emitting a compact spec and
 * expanding it here is dramatically more reliable.
 *
 * Everything here is defensive — the input is model output, so every field is
 * validated and defaulted rather than trusted.
 */

import { getIconDataUri, resolveIconName, ICON_NAMES } from './whiteboardIcons';

// ── Spec types (what the model produces) ──────────────────────────────

export interface AiNode {
  id: string;
  type?: 'box' | 'diamond' | 'ellipse' | 'text' | 'icon';
  text?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  /** Named palette colour, not a hex — keeps output on-brand. */
  color?: ColorName;
  /** For type 'icon' — a name from ICON_NAMES. */
  icon?: string;
  fontSize?: number;
}

export interface AiEdge {
  from: string;
  to: string;
  label?: string;
  /** Dashed conveys a weaker/async relationship. */
  style?: 'solid' | 'dashed';
}

export interface AiFrame {
  id?: string;
  text?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: ColorName;
}

export interface AiDiagramSpec {
  nodes?: AiNode[];
  edges?: AiEdge[];
  frames?: AiFrame[];
  /** Ids of existing scene elements the model wants removed. */
  deletions?: string[];
}

type ColorName =
  | 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'orange'
  | 'teal' | 'pink' | 'gray' | 'transparent';

// Excalidraw's own palette, so AI output is indistinguishable from
// hand-drawn shapes and stays legible on the default canvas.
const PALETTE: Record<ColorName, { stroke: string; background: string }> = {
  blue:        { stroke: '#1971c2', background: '#a5d8ff' },
  green:       { stroke: '#2f9e44', background: '#b2f2bb' },
  yellow:      { stroke: '#f08c00', background: '#ffec99' },
  red:         { stroke: '#e03131', background: '#ffc9c9' },
  purple:      { stroke: '#6741d9', background: '#d0bfff' },
  orange:      { stroke: '#e8590c', background: '#ffd8a8' },
  teal:        { stroke: '#0c8599', background: '#99e9f2' },
  pink:        { stroke: '#c2255c', background: '#fcc2d7' },
  gray:        { stroke: '#495057', background: '#e9ecef' },
  transparent: { stroke: '#1e1e1e', background: 'transparent' },
};

export const COLOR_NAMES = Object.keys(PALETTE) as ColorName[];

const DEFAULTS = {
  width: 180,
  height: 70,
  fontSize: 16,
  iconSize: 48,
};

/** Documentation of the spec, injected into the prompt. Single source of truth. */
export const SCHEMA_DOC = `
{
  "nodes": [
    {
      "id": "unique_string",
      "type": "box" | "diamond" | "ellipse" | "text" | "icon",
      "text": "Label shown on the shape",
      "x": number, "y": number,
      "width": number, "height": number,
      "color": ${COLOR_NAMES.map(c => `"${c}"`).join(' | ')},
      "icon": "only for type=icon — one of the icon names listed below"
    }
  ],
  "edges": [
    { "from": "node_id", "to": "node_id", "label": "optional", "style": "solid" | "dashed" }
  ],
  "frames": [
    { "text": "Group title", "x": n, "y": n, "width": n, "height": n, "color": "gray" }
  ]
}

AVAILABLE ICON NAMES (use exactly, for type="icon"):
${ICON_NAMES.join(', ')}
`.trim();

// ── Element construction ──────────────────────────────────────────────

let seedCounter = 1;
function nextSeed(): number {
  // Excalidraw uses seed for its hand-drawn roughness. Any positive int works;
  // it just must differ between elements or shapes render identically.
  seedCounter = (seedCounter * 1103515245 + 12345) & 0x7fffffff;
  return seedCounter;
}

function newId(prefix: string): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 11)}`;
}

/** Fields every Excalidraw element requires. */
function baseElement(overrides: Record<string, unknown>): Record<string, unknown> {
  return {
    id: newId('ai'),
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    angle: 0,
    strokeColor: '#1e1e1e',
    backgroundColor: 'transparent',
    fillStyle: 'solid',
    strokeWidth: 2,
    strokeStyle: 'solid',
    roughness: 1,
    opacity: 100,
    groupIds: [],
    frameId: null,
    roundness: null,
    seed: nextSeed(),
    version: 1,
    versionNonce: nextSeed(),
    isDeleted: false,
    boundElements: null,
    updated: Date.now(),
    link: null,
    locked: false,
    ...overrides,
  };
}

function paletteFor(color?: ColorName) {
  return PALETTE[color as ColorName] ?? PALETTE.blue;
}

/**
 * Rough text width estimate for centring labels.
 * Excalidraw measures text with canvas metrics; we approximate at ~0.55em per
 * character, which is close enough for the Virgil/Excalifont default.
 */
function estimateTextWidth(text: string, fontSize: number): number {
  const longest = text.split('\n').reduce((m, l) => Math.max(m, l.length), 0);
  return Math.max(20, longest * fontSize * 0.55);
}

/**
 * Expands an AI spec into Excalidraw elements.
 *
 * Returns the elements plus a files map for any icon images (Excalidraw keeps
 * image binaries out of `elements`, keyed by fileId).
 */
export function specToElements(spec: AiDiagramSpec): {
  elements: Record<string, unknown>[];
  files: Record<string, unknown>;
  warnings: string[];
} {
  const elements: Record<string, unknown>[] = [];
  const files: Record<string, unknown> = {};
  const warnings: string[] = [];

  // Maps the model's node ids to the real element ids we generate, so edges
  // can bind correctly.
  const idMap = new Map<string, string>();
  const geometry = new Map<string, { x: number; y: number; w: number; h: number }>();

  // ── Frames first, so they paint behind the nodes ────────────────
  for (const frame of spec.frames ?? []) {
    if (!Number.isFinite(frame.x) || !Number.isFinite(frame.y)) continue;
    const pal = paletteFor(frame.color ?? 'gray');
    const w = Math.max(60, Number(frame.width) || 300);
    const h = Math.max(60, Number(frame.height) || 200);

    elements.push(baseElement({
      type: 'rectangle',
      x: Number(frame.x),
      y: Number(frame.y),
      width: w,
      height: h,
      strokeColor: pal.stroke,
      backgroundColor: 'transparent',
      strokeStyle: 'dashed',
      strokeWidth: 1,
      roundness: { type: 3 },
      opacity: 60,
    }));

    if (frame.text) {
      elements.push(baseElement({
        type: 'text',
        x: Number(frame.x) + 12,
        y: Number(frame.y) - 26,
        width: estimateTextWidth(frame.text, 16),
        height: 22,
        text: frame.text,
        originalText: frame.text,
        fontSize: 16,
        fontFamily: 1,
        textAlign: 'left',
        verticalAlign: 'top',
        strokeColor: pal.stroke,
        lineHeight: 1.25,
        containerId: null,
        autoResize: true,
      }));
    }
  }

  // ── Nodes ───────────────────────────────────────────────────────
  for (const node of spec.nodes ?? []) {
    if (!node?.id) continue;

    const x = Number.isFinite(node.x) ? Number(node.x) : 0;
    const y = Number.isFinite(node.y) ? Number(node.y) : 0;
    const pal = paletteFor(node.color);
    const type = node.type ?? 'box';

    // ── Icon: an image element backed by an inline SVG data URI ──
    if (type === 'icon') {
      const resolved = resolveIconName(node.icon || node.text || '');
      if (!resolved) {
        warnings.push(`Unknown icon "${node.icon ?? node.text}" — skipped.`);
        continue;
      }
      const size = Math.max(24, Number(node.width) || DEFAULTS.iconSize);
      const dataUri = getIconDataUri(resolved, pal.stroke)!;
      const fileId = newId('file');

      files[fileId] = {
        mimeType: 'image/svg+xml',
        id: fileId,
        dataURL: dataUri,
        created: Date.now(),
        lastRetrieved: Date.now(),
      };

      const el = baseElement({
        type: 'image',
        x, y,
        width: size,
        height: size,
        fileId,
        status: 'saved',
        scale: [1, 1],
      });

      elements.push(el);
      idMap.set(node.id, el.id as string);
      geometry.set(node.id, { x, y, w: size, h: size });

      // Caption under the icon, when the label differs from the icon name.
      if (node.text && node.text.toLowerCase() !== resolved) {
        elements.push(baseElement({
          type: 'text',
          x: x - Math.max(0, (estimateTextWidth(node.text, 14) - size) / 2),
          y: y + size + 6,
          width: estimateTextWidth(node.text, 14),
          height: 18,
          text: node.text,
          originalText: node.text,
          fontSize: 14,
          fontFamily: 1,
          textAlign: 'center',
          verticalAlign: 'top',
          strokeColor: '#1e1e1e',
          lineHeight: 1.25,
          containerId: null,
          autoResize: true,
        }));
      }
      continue;
    }

    // ── Free text ────────────────────────────────────────────────
    if (type === 'text') {
      const text = node.text ?? '';
      const fontSize = Number(node.fontSize) || 20;
      const el = baseElement({
        type: 'text',
        x, y,
        width: estimateTextWidth(text, fontSize),
        height: fontSize * 1.25,
        text,
        originalText: text,
        fontSize,
        fontFamily: 1,
        textAlign: 'left',
        verticalAlign: 'top',
        strokeColor: pal.stroke,
        lineHeight: 1.25,
        containerId: null,
        autoResize: true,
      });
      elements.push(el);
      idMap.set(node.id, el.id as string);
      geometry.set(node.id, { x, y, w: el.width as number, h: el.height as number });
      continue;
    }

    // ── Shapes (box / diamond / ellipse) with a bound label ───────
    const w = Math.max(40, Number(node.width) || DEFAULTS.width);
    const h = Math.max(30, Number(node.height) || DEFAULTS.height);
    const shapeType = type === 'diamond' ? 'diamond' : type === 'ellipse' ? 'ellipse' : 'rectangle';

    const shapeId = newId('ai');
    const labelId = newId('ai');
    const hasLabel = Boolean(node.text);

    elements.push(baseElement({
      id: shapeId,
      type: shapeType,
      x, y,
      width: w,
      height: h,
      strokeColor: pal.stroke,
      backgroundColor: pal.background,
      roundness: shapeType === 'rectangle' ? { type: 3 } : null,
      // Excalidraw requires the container to point back at its bound label.
      boundElements: hasLabel ? [{ id: labelId, type: 'text' }] : null,
    }));

    if (hasLabel) {
      const fontSize = Number(node.fontSize) || DEFAULTS.fontSize;
      elements.push(baseElement({
        id: labelId,
        type: 'text',
        // Excalidraw recomputes these on load for bound labels; approximate
        // values keep the pre-render frame from flashing in the wrong place.
        x: x + 8,
        y: y + h / 2 - fontSize * 0.6,
        width: Math.max(20, w - 16),
        height: fontSize * 1.25,
        text: node.text,
        originalText: node.text,
        fontSize,
        fontFamily: 1,
        textAlign: 'center',
        verticalAlign: 'middle',
        strokeColor: '#1e1e1e',
        lineHeight: 1.25,
        containerId: shapeId,
        autoResize: false,
      }));
    }

    idMap.set(node.id, shapeId);
    geometry.set(node.id, { x, y, w, h });
  }

  // ── Edges ───────────────────────────────────────────────────────
  for (const edge of spec.edges ?? []) {
    const fromId = idMap.get(edge.from);
    const toId = idMap.get(edge.to);
    const a = geometry.get(edge.from);
    const b = geometry.get(edge.to);

    if (!fromId || !toId || !a || !b) {
      warnings.push(`Edge ${edge.from} → ${edge.to} skipped (unknown node).`);
      continue;
    }

    // Route from the nearest edge midpoints so the arrow doesn't cut through
    // the shapes. Excalidraw refines this once bindings are live.
    const ac = { x: a.x + a.w / 2, y: a.y + a.h / 2 };
    const bc = { x: b.x + b.w / 2, y: b.y + b.h / 2 };
    const dx = bc.x - ac.x;
    const dy = bc.y - ac.y;

    let start: { x: number; y: number };
    let end: { x: number; y: number };
    if (Math.abs(dx) > Math.abs(dy)) {
      start = { x: dx > 0 ? a.x + a.w : a.x, y: ac.y };
      end   = { x: dx > 0 ? b.x : b.x + b.w, y: bc.y };
    } else {
      start = { x: ac.x, y: dy > 0 ? a.y + a.h : a.y };
      end   = { x: bc.x, y: dy > 0 ? b.y : b.y + b.h };
    }

    const arrowId = newId('ai');
    elements.push(baseElement({
      id: arrowId,
      type: 'arrow',
      x: start.x,
      y: start.y,
      width: end.x - start.x,
      height: end.y - start.y,
      points: [[0, 0], [end.x - start.x, end.y - start.y]],
      strokeColor: '#1e1e1e',
      backgroundColor: 'transparent',
      strokeStyle: edge.style === 'dashed' ? 'dashed' : 'solid',
      roundness: { type: 2 },
      startBinding: { elementId: fromId, focus: 0, gap: 4 },
      endBinding: { elementId: toId, focus: 0, gap: 4 },
      startArrowhead: null,
      endArrowhead: 'arrow',
      elbowed: false,
    }));

    if (edge.label) {
      elements.push(baseElement({
        type: 'text',
        x: (start.x + end.x) / 2 - estimateTextWidth(edge.label, 14) / 2,
        y: (start.y + end.y) / 2 - 18,
        width: estimateTextWidth(edge.label, 14),
        height: 18,
        text: edge.label,
        originalText: edge.label,
        fontSize: 14,
        fontFamily: 1,
        textAlign: 'center',
        verticalAlign: 'top',
        strokeColor: '#495057',
        lineHeight: 1.25,
        containerId: null,
        autoResize: true,
      }));
    }
  }

  return { elements, files, warnings };
}

/**
 * Extracts a JSON object from a model response.
 *
 * Handles the usual failure modes: ```json fences, leading prose, and trailing
 * commentary. Returns null when nothing parseable is found.
 */
export function parseSpecFromResponse(raw: string): AiDiagramSpec | null {
  if (!raw) return null;

  let text = raw.replace(/```json/gi, '').replace(/```/g, '').trim();

  // Take the outermost {...} block.
  const first = text.indexOf('{');
  const last = text.lastIndexOf('}');
  if (first === -1 || last === -1 || last <= first) return null;
  text = text.slice(first, last + 1);

  try {
    const parsed = JSON.parse(text);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed as AiDiagramSpec;
  } catch {
    return null;
  }
}

/**
 * Lays out nodes the model left unpositioned.
 *
 * Models frequently omit x/y or stack everything at the origin. Rather than
 * discard the diagram, place those nodes on a readable grid below whatever was
 * positioned explicitly.
 */
export function autoLayout(spec: AiDiagramSpec, originX = 0, originY = 0): AiDiagramSpec {
  const nodes = spec.nodes ?? [];
  const unpositioned = nodes.filter(
    n => !Number.isFinite(n.x as number) || !Number.isFinite(n.y as number)
  );
  if (unpositioned.length === 0) return spec;

  const COLS = 4;
  const GAP_X = 240;
  const GAP_Y = 140;

  // Start below any explicitly positioned content so nothing overlaps.
  const positioned = nodes.filter(n => Number.isFinite(n.x as number));
  const baseY = positioned.length
    ? Math.max(...positioned.map(n => Number(n.y) + (Number(n.height) || DEFAULTS.height))) + GAP_Y
    : originY;

  unpositioned.forEach((node, i) => {
    node.x = originX + (i % COLS) * GAP_X;
    node.y = baseY + Math.floor(i / COLS) * GAP_Y;
  });

  return spec;
}
